// plan-02 S06: 48 Hz drone bed — bible S16
// Sines at 48, 96, 144, 192 Hz; LFO at 0.12 Hz; pink-noise pad
// plan-17 S04: capacitor whine (4.8 kHz sine, -52 dBFS) + retune chirp (96→47 Hz)

import { getCtx, getMasterGain } from './context'

function dbfsToGain(dbfs: number): number {
  return Math.pow(10, dbfs / 20)
}

const FREQS = [48, 96, 144, 192]
const GAINS = [-28, -34, -38, -42] // dBFS
const LFO_DEPTH = 0.04
const LFO_HZ = 0.12
const PINK_GAIN = -44
const PINK_CUTOFF = 600

// plan-17 S04: retune chirp constants
const CHIRP_START = 96
const CHIRP_END = 47
const CHIRP_DUR = 0.24 // 240ms
const CHIRP_GAIN = -38
const CHIRP_MIN_INTERVAL = 90000  // 90s
const CHIRP_MAX_INTERVAL = 180000 // 180s
const CHIRP_HSYNC_COOLDOWN = 10000 // 10s after H-sync glitch

let running = false
let droneMaster: GainNode | null = null

// plan-17 S04: retune chirp state
let lastHsyncTime = 0
let chirpTimer: ReturnType<typeof setTimeout> | null = null

// plan-17 S04: hsync glitch cooldown gate for retune chirp
export function markHsyncGlitch(): void {
  lastHsyncTime = Date.now()
}

// plan-16 S06: attenuate drone by ~6 dB while video player is open
export function setPlayerAttenuation(on: boolean): void {
  if (!droneMaster) return
  const ctx = droneMaster.context as AudioContext
  const target = on ? 0.5 : 1.0 // -6dB when player open
  droneMaster.gain.setValueAtTime(droneMaster.gain.value, ctx.currentTime)
  droneMaster.gain.linearRampToValueAtTime(target, ctx.currentTime + 0.3)
}

// tape S02: silence the drone bed entirely while a mixtape track plays; restore
// on pause/stop/album-end. The drone is the idle ambience — nothing competes
// with the music. Slower fade-in on release so the bed re-blooms gently.
export function setDroneMusicDuck(on: boolean): void {
  if (!droneMaster) return
  const ctx = droneMaster.context as AudioContext
  const target = on ? 0.0001 : 1.0
  const ramp = on ? 0.5 : 1.4
  droneMaster.gain.cancelScheduledValues(ctx.currentTime)
  droneMaster.gain.setValueAtTime(Math.max(droneMaster.gain.value, 0.0001), ctx.currentTime)
  droneMaster.gain.exponentialRampToValueAtTime(target, ctx.currentTime + ramp)
}

function createPinkNoise(ctx: AudioContext, dest: AudioNode): AudioBufferSourceNode {
  const bufferSize = ctx.sampleRate * 2
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
  const data = buffer.getChannelData(0)
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0
  for (let i = 0; i < bufferSize; i++) {
    const white = Math.random() * 2 - 1
    b0 = 0.99886 * b0 + white * 0.0555179
    b1 = 0.99332 * b1 + white * 0.0750759
    b2 = 0.96900 * b2 + white * 0.1538520
    b3 = 0.86650 * b3 + white * 0.3104856
    b4 = 0.55000 * b4 + white * 0.5329522
    b5 = -0.7616 * b5 - white * 0.0168980
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11
    b6 = white * 0.115926
  }
  const src = ctx.createBufferSource()
  src.buffer = buffer
  src.loop = true
  src.connect(dest)
  return src
}

function scheduleRetuneChirp(): void {
  if (!running) return
  const interval = CHIRP_MIN_INTERVAL + Math.random() * (CHIRP_MAX_INTERVAL - CHIRP_MIN_INTERVAL)
  chirpTimer = setTimeout(() => {
    // skip if within H-sync cooldown
    if (Date.now() - lastHsyncTime < CHIRP_HSYNC_COOLDOWN) {
      scheduleRetuneChirp()
      return
    }
    playRetuneChirp()
    scheduleRetuneChirp()
  }, interval)
}

function playRetuneChirp(): void {
  try {
    const ctx = getCtx()
    if (ctx.state !== 'running') return
    const now = ctx.currentTime
    const g = ctx.createGain()
    g.connect(getMasterGain())
    g.gain.setValueAtTime(dbfsToGain(CHIRP_GAIN), now)
    g.gain.linearRampToValueAtTime(0, now + CHIRP_DUR)
    const o = ctx.createOscillator()
    o.type = 'sine'
    o.frequency.setValueAtTime(CHIRP_START, now)
    o.frequency.linearRampToValueAtTime(CHIRP_END, now + CHIRP_DUR)
    o.connect(g)
    o.start(now)
    o.stop(now + CHIRP_DUR + 0.01)
  } catch {
    // audio cues never throw
  }
}

export function startDrone(): void {
  if (running) return
  running = true
  const ctx = getCtx()

  // master gain with LFO
  const master = ctx.createGain()
  master.gain.value = 0
  droneMaster = master
  master.connect(getMasterGain())

  // LFO for amplitude modulation
  const lfo = ctx.createOscillator()
  lfo.type = 'sine'
  lfo.frequency.value = LFO_HZ
  const lfoGain = ctx.createGain()
  lfoGain.gain.value = LFO_DEPTH
  lfo.connect(lfoGain)
  lfoGain.connect(master.gain)
  lfo.start()

  // harmonic sines
  for (let i = 0; i < FREQS.length; i++) {
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.value = FREQS[i]
    const g = ctx.createGain()
    g.gain.value = dbfsToGain(GAINS[i])
    osc.connect(g)
    g.connect(master)
    osc.start()
  }

  // pink noise pad
  const pinkGain = ctx.createGain()
  pinkGain.gain.value = dbfsToGain(PINK_GAIN)
  const pinkLp = ctx.createBiquadFilter()
  pinkLp.type = 'lowpass'
  pinkLp.frequency.value = PINK_CUTOFF
  createPinkNoise(ctx, pinkLp)
  pinkLp.connect(pinkGain)
  pinkGain.connect(master)

  // plan-17 S04: retune chirp scheduler (90-180s cadence)
  scheduleRetuneChirp()

  // fade in over 2 s
  master.gain.setValueAtTime(0, ctx.currentTime)
  master.gain.linearRampToValueAtTime(1, ctx.currentTime + 2)
}

export function stopDrone(): void {
  running = false
  if (chirpTimer) {
    clearTimeout(chirpTimer)
    chirpTimer = null
  }
}

export function isDroneRunning(): boolean {
  return running
}
